import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Divider, Form, Tooltip } from 'antd';
import { Helmet } from 'react-helmet';
import { PageProps } from '@/types/PageProps';
import { DefaultRouteTabs } from '@/components/_common';
import PurchaseReturnForm from '@/components/Movements/PurchaseReturn/PurchaseReturnForm';
import {
  ReturnItemFormValues,
  ReturnItemGenerateInvoice,
  ReturnItemQuery,
  UpdateIpiValueInInvoiceItemEnum,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { DateTimeFormat } from '@/utils/moment';
import { useItemsPurchaseReturnGenerateInvoice } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useDisclosure } from '@/hooks';
import UpdateIpiValueInvoiceItemModal from '@/components/Movements/PurchaseReturn/UpdateIpiValueInvoiceItemModal';

function PurchaseReturnPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<ReturnItemFormValues>();
  const items = Form.useWatch('items', form);
  const [generateInvoiceId, setGenerateInvoiceId] = useState<number>(0);
  const [hasKey, setHasKey] = useState<boolean>(false);
  const [isGenerateDocument, setIsGenerateDocument] = useState<boolean>(true);

  const [modalIpiValueVisible, modalIpiValueAction] = useDisclosure();

  const companyId = Form.useWatch('companyId', form);

  const { data: configDataInvoice, isLoading: loadingConfigInvoice } =
    useInvoiceSettingsByCompany(companyId);

  const [queryParams, setQueryParams] = useState<ReturnItemQuery>({
    order: 'DESC',
    rows: 10,
    ...convertParams<ReturnItemQuery>({
      order: 'string',
      companyId: 'number',
      supplierId: 'number',
      releaseDateStart: 'string',
      releaseDateEnd: 'string',
      supplierName: 'string',
    }),
  });

  const generateInvoice = useItemsPurchaseReturnGenerateInvoice();

  usePersistSearchParams(queryParams);

  const handlerSearchPurchases = async () => {
    const { supplier, ...values } = await form.validateFields();

    setQueryParams((prev) => ({
      ...prev,
      companyId: values.companyId,
      supplierId: supplier.id,
      supplierName: supplier.name,
      releaseDateStart: values.releaseDateStart?.format(DateTimeFormat),
      releaseDateEnd: values.releaseDateEnd?.format(DateTimeFormat),
    }));
  };

  const handleGenerateInvoice = async (
    generateReferencedDocument: boolean,
    updateIpiValueInInvoiceItem?: UpdateIpiValueInInvoiceItemEnum,
  ) => {
    const values = await form.validateFields();

    const purchaseItems: ReturnItemGenerateInvoice[] = values.items
      .filter((item) => Number.isFinite(item?.quantityToReturn))
      .map((item) => ({
        ...item,
        purchaseCode: item.purchase?.code,
        keyNfe: item.purchase?.keyNfe,
        purchase: item.purchase?.id,
      }));

    const response = await generateInvoice.mutateAsync({
      generateReferencedDocument,
      updateIpiValueInInvoiceItem:
        updateIpiValueInInvoiceItem ||
        UpdateIpiValueInInvoiceItemEnum.NONE_OPTIONS,
      purchaseItemsRequestDTO: purchaseItems,
    });

    setGenerateInvoiceId(response.invoiceId);

    modalIpiValueAction.close();
  };

  const disableGenerateInvoice =
    !configDataInvoice?.issueNfe ||
    items?.every((item) => item?.quantityToReturn <= 0);

  const handleMessage = () => {
    if (!!companyId) {
      if (!configDataInvoice?.issueNfe) {
        return 'Necessário configuração de NFE para a empresa selecionada';
      } else if (items?.every((item) => item?.quantityToReturn <= 0)) {
        return 'Selecione pelo menos 1 item para gerar nota e informe uma quantidade a retornar maior que zero.';
      }
    }
    return 'Preencha o filtro de pesquisa';
  };

  const handlerOnOk = (
    updateIpiValueInInvoiceItem?: UpdateIpiValueInInvoiceItemEnum,
  ) => {
    hasKey
      ? handleGenerateInvoice(isGenerateDocument, updateIpiValueInInvoiceItem)
      : handleGenerateInvoice(false, updateIpiValueInInvoiceItem);
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        persistSearchParams={false}
        permissions={route.permissions}
        route={route.parent}
      >
        <PurchaseReturnForm
          form={form}
          onCallback={handlerSearchPurchases}
          queryParams={queryParams}
          generateInvoiceId={generateInvoiceId}
          setGenerateInvoiceId={setGenerateInvoiceId}
          setHasKey={setHasKey}
        />

        {!generateInvoiceId && (
          <SpaceForButton justifyContent="end">
            <Divider />

            <Tooltip overlay={handleMessage()}>
              <Button
                type="primary"
                onClick={modalIpiValueAction.open}
                loading={generateInvoice.isLoading || loadingConfigInvoice}
                disabled={disableGenerateInvoice}
                block
                id="purchase-return-generate-invoice"
              >
                {intl.formatMessage({
                  id: 'purchaseReturn.form.button.generateInvoice',
                })}
              </Button>
            </Tooltip>
          </SpaceForButton>
        )}

        <UpdateIpiValueInvoiceItemModal
          hasKey={hasKey}
          open={modalIpiValueVisible}
          onCancel={modalIpiValueAction.close}
          onOk={handlerOnOk}
          setIsGenerateDocument={setIsGenerateDocument}
          isLoading={generateInvoice.isLoading}
        />
      </DefaultRouteTabs>
    </>
  );
}

export default PurchaseReturnPage;
